import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getLeadCalls, fetchRecording } from "@/lib/apiClient";
import { PhoneIncoming, PhoneOutgoing, Play, Loader2, Voicemail, RefreshCw, Headphones, ArrowRight, MicOff, Clock } from "lucide-react";
import { toast } from "sonner";

export const CAUSE_LABEL = {
  ANSWERED: "Beantwoord", UNANSWERED: "Niet beantwoord", VOICEMAIL: "Voicemail", BLACKLISTED: "Geblokkeerd",
  CALLCENTER: "Antwoordservice", OUTSIDE_OPERATION_TIMES: "Buiten openingstijden",
};
const STATUS_LABEL = { ringing: "Gaat over", in_progress: "In gesprek", ended: "Beëindigd" };

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const duration = (c) => {
  const s = c.duration_sec;
  if (s === null || s === undefined) return "";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const REC_MSG = {
  processing: { icon: Clock, text: "Opname wordt nog verwerkt door Rinkel — probeer het over enkele minuten opnieuw." },
  none: { icon: MicOff, text: "Geen opname beschikbaar voor dit gesprek." },
  expired: { icon: MicOff, text: "Opname is verlopen en niet meer beschikbaar bij Rinkel." },
  in_progress: { icon: Clock, text: "Gesprek is nog bezig." },
};

function RecordingPlayer({ call, kind }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
  const load = async () => {
    setLoading(true);
    setError("");
    try { setSrc(URL.createObjectURL(await fetchRecording(call.id, kind))); }
    catch (e) {
      let msg = "Opname kon niet worden geladen";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      setError(msg);
      toast.error(msg, { duration: 6000 });
    } finally { setLoading(false); }
  };
  if (src) return <audio data-testid={`audio-${kind}-${call.id}`} src={src} controls autoPlay className="w-full h-9 mt-2" />;
  const Icon = kind === "voicemail" ? Voicemail : Play;
  return (
    <div>
    <button data-testid={`play-${kind}-${call.id}`} onClick={load} disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#001C51] text-white hover:bg-[#0A337A] disabled:opacity-50 transition-colors">
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {kind === "voicemail" ? "Voicemail" : "Terugluisteren"}
    </button>
    {error && <p data-testid={`play-error-${kind}-${call.id}`} className="text-xs text-[#B0413E] mt-1.5">{error}</p>}
    </div>
  );
}

export function CallRow({ c, showLead = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const In = c.direction === "inbound";
  const DirIcon = In ? PhoneIncoming : PhoneOutgoing;
  const answered = c.cause === "ANSWERED";
  return (
    <div data-testid={`call-row-${c.id}`} className="rounded-xl border border-[#E2E7EC] bg-white p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${In ? "bg-[#EAFBFB] text-[#0C6E6E]" : "bg-[#E3E9F5] text-[#001C51]"}`}>
          <DirIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#16202B]">{In ? "Inkomend" : "Uitgaand"} · {fmtDateTime(c.started_at || c.created_at)}</p>
          <p className="text-xs text-[#5B6675]">{c.user_name || c.answered_by || "Onbekende medewerker"}{duration(c) ? ` · ${duration(c)}` : ""}{showLead ? ` · ${c.lead_name || c.external_number || "Geen lead"}` : ""}</p>
        </div>
        <span data-testid={`call-cause-${c.id}`} className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${answered ? "bg-[#D6F3F3] text-[#0C6E6E]" : c.cause ? "bg-[#F3DEDD] text-[#B0413E]" : "bg-[#EDF1F5] text-[#5B6675]"}`}>
          {CAUSE_LABEL[c.cause] || STATUS_LABEL[c.status] || "Onbekend"}
        </span>
      </div>
      {isAdmin && !c.has_recording && REC_MSG[c.recording_state] && (() => { const M = REC_MSG[c.recording_state]; return (
        <p data-testid={`rec-state-${c.id}`} className="flex items-center gap-1.5 text-xs text-[#5B6675] mt-2"><M.icon className="h-3.5 w-3.5" /> {M.text}</p>
      ); })()}
      {showLead && c.lead_id && (
        <button data-testid={`call-open-lead-${c.id}`} onClick={() => navigate(`/belomgeving/${c.lead_id}`)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#001C51] hover:underline">Open leadkaart <ArrowRight className="h-3.5 w-3.5" /></button>
      )}
      {isAdmin && (c.has_recording || c.has_voicemail) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {c.has_recording && <div className="w-full"><RecordingPlayer call={c} kind="recording" /></div>}
          {c.has_voicemail && <div className="w-full"><RecordingPlayer call={c} kind="voicemail" /></div>}
        </div>
      )}
    </div>
  );
}

export function CallHistory({ leadId }) {
  const [calls, setCalls] = useState(null);
  const load = () => getLeadCalls(leadId).then(setCalls).catch(() => setCalls([]));
  useEffect(() => { if (leadId) load(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4" data-testid="call-history">
      <div className="flex items-center gap-2 mb-3">
        <Headphones className="h-4 w-4 text-[#16BFBF]" />
        <span className="text-sm font-semibold text-[#001C51]">Rinkel-gesprekken</span>
        <button data-testid="call-history-refresh" onClick={load} title="Vernieuwen" className="ml-auto p-1.5 rounded-lg hover:bg-[#EDF1F5] text-[#5B6675]"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {calls === null ? <p className="text-sm text-[#5B6675]">Laden…</p>
        : calls.length === 0 ? <p className="text-sm text-[#5B6675]" data-testid="call-history-empty">Nog geen gesprekken via Rinkel geregistreerd.</p>
        : <div className="space-y-2">{calls.map((c) => <CallRow key={c.id} c={c} />)}</div>}
    </div>
  );
}
